import React from "react";
import { useTranslation } from "react-i18next";
import { InsightsDonutCard } from "../../../shared/layout/InsightsDonutCard";
import { InsightsRatioCard } from "../../../shared/layout/InsightsRatioCard";
import { formatTranslation } from "../../../shared/utils/translationUtils";
import {
  bucketTotal,
  formatInsightDate,
  toDonutSegments,
  toInsightCounts,
} from "./insightHelpers";
import type {
  BucketInsightsResponse,
  CountsInsightsResponse,
  TrialInsightsParams,
} from "./trialInsightsService";
import { TRIAL_INSIGHTS_CHARTS } from "./trialInsightsConfig";
import type { InsightChartConfig } from "./types";
import { useTrialInsights, type InsightQueryResult } from "./useTrialInsights";
import "./TrialSummaryInsightsPanel.less";

export interface TrialSummaryInsightsPanelProps extends TrialInsightsParams {
  /** Overridable so another page can supply a different card set. */
  charts?: InsightChartConfig[];
}

/**
 * The summary cards above the trials table.
 *
 * Every card is driven by `TRIAL_INSIGHTS_CHARTS` and shares the trials
 * filters, so the numbers always describe the same set the table lists.
 *
 * Errors are handled per card rather than hiding the whole panel: one of these
 * endpoints depends on an external service, and losing all three because that
 * one is down would be a poor trade.
 */
export const TrialSummaryInsightsPanel: React.FC<
  TrialSummaryInsightsPanelProps
> = ({ charts = TRIAL_INSIGHTS_CHARTS, ...params }) => {
  const { t, i18n } = useTranslation();
  // i18n.ts sets `returnObjects: true`, so t() resolves to `{ message }`, not
  // a string — anything rendered as text must go through formatTranslation.
  const translate = (key: string): string => formatTranslation(t(key));

  // A property-scoped endpoint cannot be answered without one, so that card
  // sits out entirely rather than rendering a meaningless "0 / 0".
  const visibleCharts = charts.filter(
    (chart) => !chart.requiresPropertyId || Boolean(params.farmId)
  );

  const results = useTrialInsights(visibleCharts, params);

  if (!params.orgId) return null;

  const errorText = translate("summaryInsights.loadError");
  const retryLabel = translate("summaryInsights.retry");

  const renderCard = (
    config: InsightChartConfig,
    result: InsightQueryResult
  ): React.ReactNode => {
    const shared = {
      id: config.id,
      // isLoading, not isPending: a disabled query reports pending forever
      // with nothing in flight, which would leave a skeleton spinning.
      loading: result.isLoading,
      error: result.isError,
      errorMessage: errorText,
      retryLabel,
      onRetry: () => {
        void result.refetch();
      },
    };

    if (config.type === "breakdown") {
      const response = result.data as BucketInsightsResponse | undefined;

      return (
        <InsightsDonutCard
          {...shared}
          key={config.id}
          title={translate(config.titleKey)}
          total={bucketTotal(response?.data)}
          segments={toDonutSegments(response, config, translate)}
        />
      );
    }

    const response = result.data as CountsInsightsResponse | undefined;
    const { value, total } = toInsightCounts(response, config);
    const dateTitle = config.titleFromRetrievedAt
      ? formatInsightDate(response?.retrieved_at, i18n?.language)
      : "";

    return (
      <InsightsRatioCard
        {...shared}
        key={config.id}
        // The API's own "today" names the card; the static key covers the gap
        // before the first response lands.
        title={dateTitle || translate(config.titleKey)}
        value={value}
        total={total}
        denominator={`/ ${total.toLocaleString()} ${translate(
          "summaryInsights.trials"
        )}`}
        caption={config.captionKey ? translate(config.captionKey) : undefined}
        variant={config.variant ?? "ring"}
        color={config.color}
      />
    );
  };

  return (
    <div
      className="trial-summary-insights"
      data-testid="trial-summary-insights"
    >
      <span className="trial-summary-insights__title">
        {translate("summaryInsights.title")}
      </span>

      <div className="trial-summary-insights__cards">
        {visibleCharts.map((config, index) =>
          renderCard(config, results[index])
        )}
      </div>
    </div>
  );
};

export default TrialSummaryInsightsPanel;
